import React, { useState } from 'react';
import { api, ApiError, setSession } from './api';
import { Session } from './types';
import { IconKey, IconUser } from './Icons';

export function Login({ onDone }: { onDone: (s: Session) => void }) {
  const [mode, setMode] = useState<'code' | 'admin'>('code');
  const [code, setCode] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recover, setRecover] = useState(false);
  const [rkey, setRkey] = useState('');
  const [npw, setNpw] = useState('');
  const [done, setDone] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      if (mode === 'admin' && recover) {
        await api.recoverAdmin(user.trim(), rkey.trim(), npw);
        setRecover(false); setRkey(''); setNpw(''); setPass('');
        setDone('Password changed. Sign in with your new password. (Make a new recovery key after signing in.)');
        return;
      }
      const s = mode === 'code' ? await api.loginCode(code.trim()) : await api.loginAdmin(user.trim(), pass);
      setSession(s);
      onDone(s);
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="masthead-big">
          <span className="kicker">Daily edition</span>
          <h1>Royal Journalism</h1>
          <span className="rule" />
        </div>
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={mode === 'code'} className={mode === 'code' ? 'on' : ''} onClick={() => setMode('code')}>
            <IconKey size={16} /> Access code
          </button>
          <button role="tab" aria-selected={mode === 'admin'} className={mode === 'admin' ? 'on' : ''} onClick={() => setMode('admin')}>
            <IconUser size={16} /> Admin
          </button>
        </div>
        <form onSubmit={submit}>
          {mode === 'code' ? (
            <>
              <label htmlFor="code">Enter your 8-character code</label>
              <input
                id="code"
                className="code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8))}
                maxLength={8}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="off"
                inputMode="text"
                placeholder="e.g. aB3kT9xQ"
                autoFocus
              />
              <p className="hint">Capital and small letters matter. The admin gives you this code. Lost it? Ask the admin to send a new one.</p>
            </>
          ) : (
            <>
              <label htmlFor="au">Admin name</label>
              <input id="au" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username" autoFocus />
              {recover ? (
                <>
                  <label htmlFor="rk">Recovery key</label>
                  <input id="rk" value={rkey} onChange={(e) => setRkey(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX-XXXX" autoCapitalize="characters" autoComplete="off" spellCheck={false} />
                  <label htmlFor="rn">New password (8+ characters)</label>
                  <input id="rn" type="password" value={npw} onChange={(e) => setNpw(e.target.value)} autoComplete="new-password" />
                  <p className="hint">The recovery key was shown once in Admin → My password. It works only once.</p>
                </>
              ) : (
                <>
                  <label htmlFor="ap">Password</label>
                  <input id="ap" type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete="new-password" />
                </>
              )}
              <p className="hint">
                <a href="#forgot" onClick={(e) => { e.preventDefault(); setErr(''); setDone(''); setRecover(!recover); }}>
                  {recover ? 'Back to sign in' : 'Forgot password?'}
                </a>
              </p>
            </>
          )}
          {done && <p className="hint" role="status">{done}</p>}
          {err && <p className="error" role="alert">{err}</p>}
          <button className="btn primary block" type="submit" disabled={busy || (mode === 'code' ? code.length !== 8 : recover ? !user || !rkey || npw.length < 8 : !user || !pass)}>
            {busy ? 'Checking…' : mode === 'admin' && recover ? 'Set new password' : 'Open the paper'}
          </button>
        </form>
      </div>
    </div>
  );
}
